import React, { useMemo, useContext } from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';
import InputField from 'components/InputField';
import AbnAcnField from 'components/AbnAcnField';
import LockclosedIcon from 'icons/Lockclosed.icon';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import validations from 'validations';
import { UNDER_EXTERNAL_ADMINISTRATION } from './utils';
import ListingContext from 'router/subrouters/ListingCreationWizard/providers/Listing/Listing.context';
import PatchListingContext from 'router/subrouters/ListingCreationWizard/providers/PatchListing/PatchListing.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import BossModeBadge from 'components/BossModeBadge';
import { EDIT_ROUTE } from 'data/global';

import './ControlledInformationCard.styles.scss';
import './ControlledInformationCard.styles.responsive.scss';

type ControlledInformationCardProps = {
  className?: string;
};

const ControlledInformationCard: React.FC<ControlledInformationCardProps> = (
  props,
) => {
  const { className } = props;
  const classes = classNames('controlled-information-card', className);
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { listing } = useContext(ListingContext);
  const { patchListingValue } = useContext(PatchListingContext);
  const { isSuperAdmin } = useContext(CurrentUserContext);
  const listingIdC = listing?._id;
  const { id: routeId } = useParams<{ id: string }>();
  const listingId = pathname.includes(EDIT_ROUTE) ? routeId : listingIdC;

  const isUnderExternalAdministration = useMemo(
    () =>
      pathname.split('/').some((el) => el === UNDER_EXTERNAL_ADMINISTRATION),
    [pathname],
  );

  if (!isSuperAdmin) return null;

  return (
    <div className={classes}>
      <h4 className="controlled-information-card__top__heading">
        {t('controlledInformationTitle')}
        <BossModeBadge />
      </h4>
      <div className="controlled-information-card__top">
        <div>
          <LockclosedIcon />
        </div>
        <div>
          <p className="controlled-information-card__top__heading">
            {t('resolveUsesControlledInformation')}
          </p>
          <p>{t('resolveWillNeverReleaseControlledInformation')}</p>
          <p>{t('thisWillNotAppearControlledInformation')}</p>
        </div>
      </div>
      <div className="controlled-information-card__bottom">
        {!isUnderExternalAdministration && (
          <Field
            name="controlledInformation.ABN"
            numberType="ABN"
            listingId={listingId}
            validate={validations.exactLength(11, t('abnMustHave11Numbers'))}
            component={AbnAcnField}
            required={false}
            customOnBlur={(
              e: React.FocusEvent<HTMLInputElement>,
              isValid: boolean,
            ) => {
              if (isValid) {
                patchListingValue({
                  controlledInformation: { ABN: +e.target.value || null },
                });
              }
            }}
          />
        )}
        <Field
          name="controlledInformation.postcode"
          label={t('postcode')}
          type="number"
          placeholder={t('enterCompanyPostcode')}
          validate={validations.composeValidators(
            validations.auPostalCode(t('postalCodeInvalid')),
          )}
          component={InputField}
          customOnBlur={(
            e: React.FocusEvent<HTMLInputElement>,
            isValid: boolean,
          ) => {
            if (isValid) {
              patchListingValue({
                controlledInformation: { postcode: +e.target.value || null },
              });
            }
          }}
        />
      </div>
    </div>
  );
};

export default ControlledInformationCard;
